import { useDispatch, useReduxState } from 'hooks';
import { actions } from 'store';
import { DropdownSelectable } from 'components/common/dropdown';

import type { FilterCompletionStatus } from 'shared-types';
import type { PageState } from 'types';

type UpdateObj = {
  page: number;
  achievementCompletionStatus: Extract<
    FilterCompletionStatus,
    'all' | 'yes' | 'no' | 'expired' | 'expiring'
  >;
  completionStatus: FilterCompletionStatus;
  pathCompletionStatus: Extract<FilterCompletionStatus, 'all' | 'yes' | 'no'>;
};

interface Props {
  align?: 'left' | 'right';
  disabled: boolean;
  updateUsers: (obj: UpdateObj) => Promise<void>;
  view: PageState['view'];
}

const UsersCompletionFilter = ({
  align = 'right',
  disabled,
  updateUsers,
  view
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: {
      achievementCompletionStatus,
      achievementId,
      completionStatus,
      courseId,
      pathCompletionStatus,
      pathId
    }
  } = useReduxState();

  const handleChange = async (completionStatus) => {
    dispatch(
      actions.updatePageState({
        achievementCompletionStatus: completionStatus,
        completionStatus,
        pathCompletionStatus: completionStatus
      })
    );
    await updateUsers({
      page: 1,
      achievementCompletionStatus: completionStatus,
      completionStatus,
      pathCompletionStatus: completionStatus
    });
  };

  const options = [
    {
      text: messages.commonAll,
      value: 'all'
    },
    {
      text: messages.commonCompleted,
      value: 'yes'
    },
    {
      text: messages.tasksUsersNotCompleted,
      value: 'no'
    }
  ];
  if (view === 'achievement')
    options.splice(
      2,
      0,
      { text: messages.commonValid, value: 'valid' },
      { text: messages.commonExpiring, value: 'expiring' },
      { text: messages.commonExpired, value: 'expired' }
    );
  if (view === 'course')
    options.splice(
      2,
      0,
      ...[
        { text: messages.commonValid, value: 'valid' },
        { text: messages.commonExpiring, value: 'expiring' },
        { text: messages.commonExpired, value: 'expired' },
        { text: messages.readerCompletionFailed, value: 'failed' },
        {
          text: messages.completionsWaitingForReviews,
          value: 'waitingForReviews'
        },
        { text: messages.commonInProgress, value: 'inProgress' },
        { text: messages.tasksUsersNotStartedSingular, value: 'notStarted' }
      ]
    );

  return (
    <div
      className="completion"
      {...(disabled
        ? {
            'aria-label': messages.usersFiltersSelectCourseOrPathOrAchievement,
            'data-tooltip': messages.usersFiltersSelectCourseOrPathOrAchievement
          }
        : {})}
    >
      <DropdownSelectable
        align={align}
        category="lighter"
        defaultText={`${messages.commonSelect}...`}
        defaultValue={
          courseId && view === 'course'
            ? completionStatus
            : pathId && view === 'path'
            ? pathCompletionStatus
            : achievementId && view === 'achievement'
            ? achievementCompletionStatus
            : 'all'
        }
        disabled={disabled}
        dropdownTextMinWidth="120px"
        onChange={handleChange}
        options={options}
        selectedValueBefore={`${messages.commonCompletion}:`}
        showSelectedText={true}
        size="header"
        titleMaxWidth={120}
        value={completionStatus}
      />
    </div>
  );
};

export default UsersCompletionFilter;
